<template>
  <div v-if="rendered" class="wa-root" @click="menuAberto = null">
    <div class="wa-layout">
      <!-- SIDEBAR -->
      <aside class="wa-sidebar">
        <div class="wa-sidebar-header">
          <div class="wa-sidebar-title">Mensagens</div>
          <span class="wa-sidebar-count">{{ conversas.length }}</span>
        </div>

        <div class="wa-search">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            v-model="searchQuery"
            placeholder="Pesquisar…"
            class="wa-search-input"
          />
        </div>

        <div class="wa-conv-list">
          <div v-if="!conversasFiltradas.length" class="wa-empty-list">
            <svg
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              />
            </svg>
            <p>Sem conversas</p>
          </div>

          <div
            v-for="c in conversasFiltradas"
            :key="c.id"
            class="wa-conv-item"
            :class="{ active: conversaAtiva && conversaAtiva.id === c.id }"
            @click="abrirConversa(c)"
          >
            <div
              class="wa-conv-avatar"
              :style="'background:' + getAvatarColor(c)"
            >
              {{ getNomeIniciais(c) }}
            </div>
            <div class="wa-conv-body">
              <div class="wa-conv-top">
                <span class="wa-conv-nome">{{ getNomeDisplay(c) }}</span>
                <span class="wa-conv-hora">{{
                  formatarHora(c.ultima_interacao_em)
                }}</span>
              </div>
              <div class="wa-conv-bottom">
                <span class="wa-conv-preview">{{ c.ultima_mensagem }}</span>
                <span v-if="c.nao_lidas > 0" class="wa-badge">{{
                  c.nao_lidas
                }}</span>
              </div>
            </div>

            <div class="wa-menu-wrap" @click.stop>
              <button class="wa-menu-btn" @click.stop="toggleMenu(c.id)">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>
              <transition name="menu-pop">
                <div v-if="menuAberto === c.id" class="wa-dropdown" @click.stop>
                  <button
                    v-if="c.qr_code"
                    class="wa-dropdown-item"
                    @click="verCliente(c)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Ver cliente
                  </button>
                  <button
                    class="wa-dropdown-item danger"
                    @click="apagarChat(c)"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                    Apagar conversa
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </aside>

      <!-- CHAT -->
      <main class="wa-chat" v-if="conversaAtiva">
        <div class="wa-chat-header">
          <div
            class="wa-chat-avatar"
            :style="'background:' + getAvatarColor(conversaAtiva)"
          >
            {{ getIniciais() }}
          </div>
          <div class="wa-chat-meta">
            <div class="wa-chat-nome">{{ getNomeDisplay(conversaAtiva) }}</div>
            <div class="wa-chat-sub">
              {{ formatarTelefone(conversaAtiva.telefone)
              }}<span v-if="conversaAtiva.email">
                · {{ conversaAtiva.email }}</span
              >
            </div>
          </div>
        </div>

        <div class="wa-messages" ref="chatContainer">
          <div
            v-for="m in mensagensSemReacoes"
            :key="m.id"
            class="wa-msg-row"
            :class="m.direcao"
          >
            <div class="wa-bubble" :class="m.direcao">
              <template v-if="m.tipo === 'image'">
                <img
                  :src="getImageLink(m)"
                  class="wa-img"
                  @click="abrirLightbox(getImageLink(m))"
                />
                <div v-if="getImageCaption(m)" class="wa-img-caption">
                  {{ getImageCaption(m) }}
                </div>
              </template>
              <template v-else>
                <span class="wa-bubble-text">{{ extrairTexto(m) }}</span>
              </template>
              <div class="wa-bubble-meta">
                <span>{{ formatarHora(m.created_at) }}</span>
                <span
                  v-if="m.direcao === 'out'"
                  class="wa-status"
                  :class="m.status"
                  >{{ statusIcon(m.status) }}</span
                >
              </div>
              <div
                v-if="getReactionForMessage(m.message_id)"
                class="wa-reaction"
              >
                {{ getReactionForMessage(m.message_id) }}
              </div>
            </div>
          </div>
        </div>

        <transition name="slide-up">
          <div v-if="imagemPreview" class="wa-img-preview">
            <img :src="imagemPreview" class="wa-preview-img" />
            <button class="wa-preview-remove" @click="removerPreview">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </transition>

        <div class="wa-input-bar">
          <button class="wa-attach-btn" @click="$refs.fileInput.click()">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            >
              <path
                d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
              />
            </svg>
          </button>
          <input
            type="file"
            ref="fileInput"
            accept="image/png,image/jpeg"
            style="display: none"
            @change="handleFileSelect"
          />
          <input
            v-model="novaMensagem"
            class="wa-text-input"
            placeholder="Escrever mensagem…"
            @keyup.enter="enviarMensagem"
          />
          <button
            class="wa-send-btn"
            :class="{ active: novaMensagem.trim() || imagemFile }"
            :disabled="enviando"
            @click="enviarMensagem"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </main>

      <div v-else class="wa-empty-chat">
        <div class="wa-empty-chat-inner">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            />
          </svg>
          <h3>As tuas mensagens</h3>
          <p>Seleciona uma conversa para começar</p>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="lightboxImagem" class="wa-lightbox" @click="fecharLightbox">
        <button class="wa-lightbox-close" @click="fecharLightbox">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        <img :src="lightboxImagem" class="wa-lightbox-img" @click.stop />
      </div>
    </transition>
  </div>

  <LoadingOverlay v-else />
</template>

<script>
  import LoadingOverlay from "@/components/Loading/LoadingOverlay";
  import { whatsappService } from "@/services";

  const AVATAR_COLORS = [
    "#d4cfc9",
    "#c8c2bc",
    "#ddd8d2",
    "#c5bfba",
    "#cfc9c3",
    "#d8d2cc",
    "#c2bcb6",
    "#dad4ce",
  ];

  export default {
    components: { LoadingOverlay },
    data() {
      return {
        rendered: false,
        conversas: [],
        conversaAtiva: null,
        mensagens: [],
        novaMensagem: "",
        enviando: false,
        imagemPreview: null,
        imagemFile: null,
        menuAberto: null,
        lightboxImagem: null,
        searchQuery: "",
      };
    },
    computed: {
      mensagensSemReacoes() {
        return this.mensagens.filter((m) => m.tipo !== "reaction");
      },
      conversasFiltradas() {
        if (!this.searchQuery.trim()) return this.conversas;
        const q = this.searchQuery.toLowerCase();
        return this.conversas.filter(
          (c) =>
            (c.cliente_nome || c.nome || "").toLowerCase().includes(q) ||
            (c.telefone || "").includes(q),
        );
      },
    },
    methods: {
      toggleMenu(id) {
        this.menuAberto = this.menuAberto === id ? null : id;
      },
      getNomeDisplay(c) {
        return c.cliente_nome || c.nome || this.formatarTelefone(c.telefone);
      },
      getNomeIniciais(c) {
        const n = c.cliente_nome || c.nome || "";
        if (!n) return "?";
        const p = n.trim().split(" ");
        return p.length === 1
          ? p[0][0].toUpperCase()
          : (p[0][0] + p[p.length - 1][0]).toUpperCase();
      },
      getAvatarColor(c) {
        const s = c.cliente_nome || c.nome || c.telefone || "";
        let h = 0;
        for (let i = 0; i < s.length; i++)
          h = (h * 31 + s.charCodeAt(i)) % AVATAR_COLORS.length;
        return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
      },
      getIniciais() {
        const n =
          (this.conversaAtiva &&
            (this.conversaAtiva.cliente_nome || this.conversaAtiva.nome)) ||
          "";
        if (!n) return "?";
        const p = n.trim().split(" ");
        return p.length === 1
          ? p[0][0].toUpperCase()
          : (p[0][0] + p[p.length - 1][0]).toUpperCase();
      },
      formatarTelefone(numero) {
        if (!numero) return "";
        numero = numero.replace(/\D/g, "");
        if (numero.length < 9) return numero;
        const ind = numero.slice(0, numero.length - 9);
        const corpo = numero.slice(-9);
        return (
          "+" +
          ind +
          " " +
          corpo.slice(0, 3) +
          " " +
          corpo.slice(3, 6) +
          " " +
          corpo.slice(6)
        );
      },
      formatarHora(data) {
        if (!data) return "";
        const d = new Date(data);
        const hoje = new Date();
        const ontem = new Date(hoje);
        ontem.setDate(hoje.getDate() - 1);
        if (d.toDateString() === hoje.toDateString())
          return d.toLocaleTimeString("pt-PT", {
            hour: "2-digit",
            minute: "2-digit",
          });
        if (d.toDateString() === ontem.toDateString()) return "Ontem";
        return d.toLocaleDateString("pt-PT", {
          day: "2-digit",
          month: "2-digit",
        });
      },
      statusIcon(s) {
        return s === "sent"
          ? "✓"
          : s === "delivered" || s === "read"
          ? "✓✓"
          : s === "failed"
          ? "✕"
          : "";
      },
      extrairTexto(m) {
        try {
          const c = JSON.parse(m.conteudo);
          return m.tipo === "text" ? c.text && c.text.body : m.tipo;
        } catch {
          return "";
        }
      },
      getImageLink(m) {
        try {
          return (
            JSON.parse(m.conteudo).image && JSON.parse(m.conteudo).image.link
          );
        } catch {
          return "";
        }
      },
      getImageCaption(m) {
        try {
          return (
            JSON.parse(m.conteudo).image && JSON.parse(m.conteudo).image.caption
          );
        } catch {
          return "";
        }
      },
      getReactionForMessage(messageId) {
        const reactions = this.mensagens.filter((m) => {
          if (m.tipo !== "reaction") return false;
          try {
            return (
              JSON.parse(m.conteudo).reaction &&
              JSON.parse(m.conteudo).reaction.message_id === messageId
            );
          } catch {
            return false;
          }
        });
        if (!reactions.length) return null;
        try {
          const c = JSON.parse(reactions[reactions.length - 1].conteudo);
          return (c.reaction && c.reaction.emoji) || null;
        } catch {
          return null;
        }
      },
      abrirLightbox(url) {
        this.lightboxImagem = url;
      },
      fecharLightbox() {
        this.lightboxImagem = null;
      },
      handleFileSelect(event) {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
          this.$notify({
            type: "danger",
            message: "Imagem demasiado grande (máx 5MB)",
          });
          return;
        }
        this.imagemFile = file;
        this.imagemPreview = URL.createObjectURL(file);
      },
      removerPreview() {
        this.imagemPreview = null;
        this.imagemFile = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = null;
      },
      scrollToBottom() {
        this.$nextTick(() => {
          const c = this.$refs.chatContainer;
          if (c) c.scrollTop = c.scrollHeight;
        });
      },
      async apagarChat(conversa) {
        await whatsappService.apagarConversa(conversa.id);
        this.conversas = this.conversas.filter((c) => c.id !== conversa.id);
        if (this.conversaAtiva && this.conversaAtiva.id === conversa.id)
          this.conversaAtiva = null;
        this.menuAberto = null;
      },
      verCliente(conversa) {
        this.menuAberto = null;
        this.$router.push({
          path: "/clientes/lista-clientes",
          query: { cliente: conversa.qr_code },
        });
      },
      async carregarConversas() {
        this.conversas = await whatsappService.getConversas();
      },
      async abrirConversa(conversa) {
        this.conversaAtiva = conversa;
        this.mensagens = await whatsappService.getMensagens(conversa.id);
        await whatsappService.marcarLida(conversa.id);
        const c = this.conversas.find((x) => x.id === conversa.id);
        if (c) c.nao_lidas = 0;
        this.scrollToBottom();
      },
      async enviarMensagem() {
        if ((!this.novaMensagem.trim() && !this.imagemFile) || this.enviando)
          return;
        this.enviando = true;
        try {
          let imageUrl = null;
          if (this.imagemFile) {
            const formData = new FormData();
            formData.append("imagem", this.imagemFile);
            await whatsappService.uploadImagem(formData);
            imageUrl =
              "https://abitue.yourcode.pt/uploads/SAV5-SAW9-58SW-S62A/07gijhi2m7mg1761925330271.png";
          }
          await whatsappService.enviarMensagem({
            conversa_id: this.conversaAtiva.id,
            tipo: imageUrl ? "image" : "text",
            mensagem: this.novaMensagem,
            link: imageUrl,
            caption: this.novaMensagem || "",
          });
          this.mensagens.push({
            id: Date.now(),
            direcao: "out",
            tipo: imageUrl ? "image" : "text",
            conteudo: JSON.stringify(
              imageUrl
                ? { image: { link: imageUrl, caption: this.novaMensagem } }
                : { text: { body: this.novaMensagem } },
            ),
            created_at: new Date(),
            status: "sent",
          });
          this.novaMensagem = "";
          this.removerPreview();
          this.scrollToBottom();
        } catch (err) {
          console.error(err);
          this.$notify({
            type: "danger",
            title: "Erro",
            message: "Falha ao enviar mensagem",
          });
        }
        this.enviando = false;
      },
    },
    async mounted() {
      await this.carregarConversas();
      this.rendered = true;
    },
  };
</script>

<style scoped>
  .wa-root {
    height: calc(100vh - 80px);
    min-height: 500px;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
    background: #f7f5f3;
    padding: 20px 24px;
  }
  .wa-layout {
    display: flex;
    height: 100%;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  /* SIDEBAR */
  .wa-sidebar {
    width: 300px;
    flex-shrink: 0;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #f0ece8;
  }
  .wa-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 22px 20px 14px;
  }
  .wa-sidebar-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a1210;
    letter-spacing: -0.3px;
  }
  .wa-sidebar-count {
    background: #ff825d;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 20px;
    min-width: 22px;
    text-align: center;
  }
  .wa-search {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 14px 12px;
    background: #f7f4f1;
    border-radius: 10px;
    padding: 9px 12px;
    color: #b0a8a0;
  }
  .wa-search-input {
    background: none;
    border: none;
    outline: none;
    color: #1a1210;
    font-size: 13px;
    width: 100%;
  }
  .wa-search-input::placeholder {
    color: #b0a8a0;
  }
  .wa-conv-list {
    flex: 1;
    overflow-y: auto;
  }
  .wa-conv-list::-webkit-scrollbar {
    width: 4px;
  }
  .wa-conv-list::-webkit-scrollbar-thumb {
    background: #e8e4e0;
    border-radius: 2px;
  }
  .wa-empty-list {
    padding: 48px 20px;
    text-align: center;
    color: #c4bdb8;
  }
  .wa-empty-list p {
    font-size: 13px;
    margin: 0;
    margin-top: 10px;
  }
  .wa-conv-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f5f2ef;
    position: relative;
    transition: background 0.12s;
  }
  .wa-conv-item:hover {
    background: #faf8f6;
  }
  .wa-conv-item.active {
    background: #fff5f0;
    border-left: 3px solid #ff825d;
  }
  .wa-conv-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #5a4f48;
    flex-shrink: 0;
  }
  .wa-conv-body {
    flex: 1;
    min-width: 0;
  }
  .wa-conv-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 3px;
  }
  .wa-conv-nome {
    font-size: 13.5px;
    font-weight: 600;
    color: #1a1210;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
  }
  .wa-conv-hora {
    font-size: 11px;
    color: #b0a8a0;
    flex-shrink: 0;
  }
  .wa-conv-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .wa-conv-preview {
    font-size: 12px;
    color: #a09890;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }
  .wa-badge {
    background: #ff825d;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 10px;
    flex-shrink: 0;
  }
  .wa-menu-wrap {
    position: relative;
  }
  .wa-menu-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #c4bdb8;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    opacity: 0;
    transition: opacity 0.15s, color 0.15s;
  }
  .wa-conv-item:hover .wa-menu-btn {
    opacity: 1;
  }
  .wa-menu-btn:hover {
    color: #5a534f;
    background: #f5f2ef;
  }
  .wa-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: #fff;
    border-radius: 10px;
    min-width: 170px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    z-index: 100;
    overflow: hidden;
  }
  .wa-dropdown-item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 11px 14px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13.5px;
    color: #374151;
    transition: background 0.12s;
  }
  .wa-dropdown-item:hover {
    background: #f9f7f5;
  }
  .wa-dropdown-item.danger {
    color: #ef4444;
  }
  .wa-dropdown-item.danger:hover {
    background: #fef2f2;
  }
  /* CHAT */
  .wa-chat {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #f9f7f5;
    min-width: 0;
  }
  .wa-chat-header {
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 14px 20px;
    background: #fff;
    border-bottom: 1px solid #f0ece8;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  }
  .wa-chat-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    color: #5a4f48;
    flex-shrink: 0;
  }
  .wa-chat-nome {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.2px;
  }
  .wa-chat-sub {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 1px;
  }
  .wa-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .wa-messages::-webkit-scrollbar {
    width: 5px;
  }
  .wa-messages::-webkit-scrollbar-thumb {
    background: #e5e0db;
    border-radius: 3px;
  }
  .wa-msg-row {
    display: flex;
  }
  .wa-msg-row.in {
    justify-content: flex-start;
  }
  .wa-msg-row.out {
    justify-content: flex-end;
  }
  .wa-bubble {
    max-width: 62%;
    padding: 9px 13px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.45;
    position: relative;
    word-break: break-word;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  }
  .wa-bubble.in {
    background: #fff;
    border-bottom-left-radius: 4px;
    color: #1f2937;
  }
  .wa-bubble.out {
    background: #fff0eb;
    border-bottom-right-radius: 4px;
    color: #1f2937;
  }
  .wa-bubble-text {
    display: block;
  }
  .wa-bubble-meta {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 4px;
    font-size: 10.5px;
    color: #9ca3af;
    line-height: 1;
  }
  .wa-status.read {
    color: #3b82f6;
  }
  .wa-status.failed {
    color: #ef4444;
  }
  .wa-img {
    max-width: 220px;
    border-radius: 10px;
    display: block;
    cursor: zoom-in;
    transition: opacity 0.15s;
  }
  .wa-img:hover {
    opacity: 0.9;
  }
  .wa-img-caption {
    font-size: 12.5px;
    color: #6b7280;
    margin-top: 5px;
  }
  .wa-reaction {
    position: absolute;
    bottom: -8px;
    right: -8px;
    background: #fff;
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 14px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }
  .wa-bubble.in .wa-reaction {
    right: auto;
    left: -8px;
  }
  .wa-img-preview {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px;
    background: #f3f0ec;
    border-top: 1px solid #ede8e2;
    position: relative;
  }
  .wa-preview-img {
    max-height: 80px;
    border-radius: 8px;
  }
  .wa-preview-remove {
    position: absolute;
    top: 8px;
    right: 14px;
    background: #374151;
    border: none;
    cursor: pointer;
    color: #fff;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .wa-input-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid #f0ece8;
  }
  .wa-attach-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    padding: 6px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;
  }
  .wa-attach-btn:hover {
    color: #ff825d;
    background: #fff0eb;
  }
  .wa-text-input {
    flex: 1;
    background: #f4f1ee;
    border: none;
    border-radius: 22px;
    padding: 10px 16px;
    font-size: 14px;
    color: #1f2937;
    outline: none;
    transition: background 0.15s;
  }
  .wa-text-input:focus {
    background: #ece8e4;
  }
  .wa-text-input::placeholder {
    color: #b0a8a0;
  }
  .wa-send-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e5e0db;
    color: #9ca3af;
    flex-shrink: 0;
    transition: all 0.2s cubic-bezier(0.22, 0.68, 0, 1.2);
  }
  .wa-send-btn.active {
    background: #ff825d;
    color: #fff;
    box-shadow: 0 3px 10px rgba(255, 130, 93, 0.4);
    transform: scale(1.05);
  }
  .wa-send-btn:disabled {
    opacity: 0.6;
    cursor: default;
    transform: none;
  }
  .wa-empty-chat {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9f7f5;
  }
  .wa-empty-chat-inner {
    text-align: center;
    color: #c4bdb8;
  }
  .wa-empty-chat-inner h3 {
    font-size: 17px;
    font-weight: 600;
    color: #9ca3af;
    margin: 16px 0 6px;
  }
  .wa-empty-chat-inner p {
    font-size: 13px;
    color: #c4bdb8;
    margin: 0;
  }
  /* Lightbox */
  .wa-lightbox {
    position: fixed;
    inset: 0;
    background: rgba(10, 8, 6, 0.9);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }
  .wa-lightbox-close {
    position: absolute;
    top: 20px;
    right: 24px;
    background: rgba(255, 255, 255, 0.12);
    border: none;
    cursor: pointer;
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
  }
  .wa-lightbox-close:hover {
    background: rgba(255, 255, 255, 0.22);
  }
  .wa-lightbox-img {
    max-width: 88vw;
    max-height: 88vh;
    border-radius: 12px;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  }
  /* Animations */
  .menu-pop-enter-active {
    transition: all 0.15s cubic-bezier(0.22, 0.68, 0, 1.4);
  }
  .menu-pop-leave-active {
    transition: all 0.1s ease;
  }
  .menu-pop-enter,
  .menu-pop-leave-to {
    opacity: 0;
    transform: scale(0.9) translateY(-4px);
  }
  .slide-up-enter-active {
    transition: all 0.2s ease;
  }
  .slide-up-leave-active {
    transition: all 0.15s ease;
  }
  .slide-up-enter,
  .slide-up-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  /* Mobile */
  @media (max-width: 768px) {
    .wa-root {
      padding: 0;
      height: 100vh;
    }
    .wa-layout {
      border-radius: 0;
    }
    .wa-sidebar {
      width: 100%;
    }
    .wa-bubble {
      max-width: 82%;
    }
  }
</style>
